/**
 * PATH: packages/bills/src/components/BookkeepingWorkspace.tsx
 * FILE_ID: BILLS-PKG-CMP-WORKSPACE-001
 * PURPOSE: Sayoni-style 3-pane bookkeeping workspace.
 *
 * WHY: Bookkeepers need source document, extracted data, and the bill
 *      list visible at once. Splitting the screen into a fixed 3-pane
 *      layout — bills list (left) · document (centre) · data form
 *      (right) — is the design the user approved when reviewing
 *      `/book_keeping` in Sayoni's prototype.
 *
 * FLOW:
 *   ┌────────────┐ ┌──────────────┐ ┌──────────────┐
 *   │ BillsList  │ │ DocumentView │ │ BillDataForm │
 *   │  (filter,  │→│  (PDF/image) │ │ (edit, save, │
 *   │   select)  │ │              │ │  approve…)   │
 *   └────────────┘ └──────────────┘ └──────────────┘
 *           ↓                ↑                ↑
 *      onSelect()    bill.attachment_id   onSave()
 *           ↓                                 ↓
 *           └───── selectedBill ──────────────┘
 *
 * DEPENDENCIES:
 *   - useBillsList / useBillDetail / useBillActions (./hooks)
 *     — these read the injected BillsClient.
 *   - <BillsListPanel/> <DocumentViewer/> <BillDataPanel/>
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { FileText, PanelLeftClose, PanelLeftOpen, PanelRightOpen, Rows2, X } from 'lucide-react';
import { ErrorBoundary } from '@quotech/ui';
import {
  getBillDuplicateConflict,
  useBillActions,
  useBillDetail,
  useBillsList,
} from '../hooks';
import type {
  BillDetail,
  BillEditablePatch,
  BillFlag,
  BillListRow,
  BillStatus,
  BillStatusFilter,
  MarkBillPaidInput,
  SetSupplierTermInput,
} from '../types';
import type { BulkAction } from './billsQueueModel';
import { BillDataPanel } from './BillDataPanel';
import { BillEmailPanel } from './BillEmailPanel';
import { BillPoPanel } from './BillPoPanel';
import { BillsListPanel } from './BillsListPanel';
import { DocumentViewer } from './DocumentViewer';
import { StructuredBillRead } from './StructuredBillRead';
import { ResizeHandle, usePersistentBool, usePersistentSize } from './panelResize';
import { readBillsLayoutPrefs } from '../feature/billsLayoutPrefs';
import { billFieldValue, parseLineFieldKey, type BillFieldKey } from './billFieldKeys';
import { pickDefaultBillId } from './defaultBillSelection';
import { routePickedText, takeStashForField } from './captureRouting';

type RightTab = 'bill' | 'read' | 'email' | 'po';
/** What the optional centre bottom-split shows below the document. */
type CentreSplit = 'none' | 'email' | 'read';

/**
 * Run a money action (approve / mark-paid / push-to-xero) with the
 * duplicate-bill backstop UX: when the backend answers 409
 * ``possible_duplicate``, show the other bill's details in a native
 * confirm (the same affordance DashboardPage/CfoJobLinkingPage use)
 * and, if the operator accepts, re-run the action with ``force``.
 * Declining resolves to null — identical to any other no-op action.
 */
async function runWithDuplicateConfirm<T>(
  label: string,
  run: (force: boolean) => Promise<T | null>,
): Promise<T | null> {
  try {
    return await run(false);
  } catch (e) {
    const dup = getBillDuplicateConflict(e);
    if (!dup) return null;
    const money = dup.total != null ? ` for $${dup.total}` : '';
    const paidLine = dup.payment_status === 'paid'
      ? `It was already marked PAID${dup.paid_at ? ` on ${dup.paid_at}` : ''}.`
      : `Its status is "${dup.status}" (${dup.payment_status}).`;
    const ok = window.confirm(
      'Possible duplicate bill.\n\n'
      + `"${dup.vendor_name}" #${dup.invoice_number}${money} already exists. `
      + paidLine
      + `\n\n${label} this bill anyway?`,
    );
    if (!ok) return null;
    return run(true);
  }
}

/**
 * Isolates one of the three workspace panes behind an error boundary so a
 * render throw in (say) the PDF viewer or the edit form cannot blank the
 * whole hub. ``resetKey`` (the selected bill id) clears a crashed pane when
 * the operator moves to another bill, so recovery doesn't need a full reload.
 */
function PaneBoundary({
  name,
  resetKey,
  children,
}: {
  name: string;
  resetKey?: string | null;
  children: ReactNode;
}) {
  return (
    <ErrorBoundary
      resetKeys={[resetKey]}
      fallback={
        <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 p-6 text-center text-sm">
          <p className="font-medium text-foreground">
            The {name} panel hit an error.
          </p>
          <p className="text-xs text-muted-foreground">
            Select another bill, or refresh the page to recover.
          </p>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}

/*
 * Drag-resizable rails + email split (jake 2026-06-18 — "I should be
 * able to expand the right side bar… and split to see the email below").
 * Widths/heights persist per-browser so a bookkeeper's chosen layout
 * survives reloads. Defaults match the previous fixed Tailwind widths
 * (left ~w-72, right ~between w-80/w-96) so nothing jumps on first load.
 */
const LEFT_RAIL_KEY = 'quotech.bills.ws.leftRailW';
const LEFT_COLLAPSED_KEY = 'quotech.bills.ws.leftCollapsed';
const RIGHT_RAIL_KEY = 'quotech.bills.ws.rightRailW';
const EMAIL_SPLIT_KEY = 'quotech.bills.ws.emailSplitH';
const DEFAULT_LEFT_RAIL = 288;
const DEFAULT_RIGHT_RAIL = 360;
const DEFAULT_EMAIL_SPLIT = 300;
const LEFT_RAIL_MIN = 220;
const LEFT_RAIL_MAX = 520;
const RIGHT_RAIL_MIN = 300;
const RIGHT_RAIL_MAX = 720;
const EMAIL_SPLIT_MIN = 160;
const EMAIL_SPLIT_MAX = 760;

interface Props {
  /**
   * Initial status filter for the list. Defaults to 'extracted'
   * because that's the bookkeeper's primary triage queue.
   */
  initialStatus?: BillStatus | 'all';
  /**
   * Optional initial bill ID to select (e.g. from URL ?id= param).
   */
  initialBillId?: string | null;
  /**
   * Optional callback when the selected bill changes — apps can sync
   * the URL with this to support deep-linking and back/forward.
   */
  onSelectionChange?: (billId: string | null) => void;
  /**
   * Polling interval (ms) for the list refresh. Defaults to 30s so
   * newly-extracted bills appear without a manual Refresh click —
   * the AI extraction pipeline can take 10-60s and bookkeepers
   * complained they had to keep clicking Refresh.
   *
   * Set to 0 to disable polling (e.g. for tests).
   */
  refetchIntervalMs?: number;
}

export function BookkeepingWorkspace({
  initialStatus = 'extracted',
  initialBillId = null,
  onSelectionChange,
  refetchIntervalMs = 30_000,
}: Props) {
  const [status, setStatus] = useState<BillStatusFilter>(initialStatus);
  // Attention-reason filter (queue redesign) — orthogonal to status. The
  // header keeps them mutually exclusive so exactly one filter is live.
  const [flag, setFlag] = useState<BillFlag | undefined>(undefined);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedId, setSelectedIdRaw] = useState<string | null>(initialBillId);
  const [rightTab, setRightTab] = useState<RightTab>('bill');

  // Resizable layout (persisted) + the email-below-document split.
  const [leftRailW, setLeftRailW] = usePersistentSize(LEFT_RAIL_KEY, DEFAULT_LEFT_RAIL);
  const [leftCollapsed, setLeftCollapsed] = usePersistentBool(LEFT_COLLAPSED_KEY, false);
  const [rightRailW, setRightRailW] = usePersistentSize(RIGHT_RAIL_KEY, DEFAULT_RIGHT_RAIL);
  const [emailSplitH, setEmailSplitH] = usePersistentSize(EMAIL_SPLIT_KEY, DEFAULT_EMAIL_SPLIT);
  // The centre can split vertically to show the source email OR the
  // structured read below the document (generalised from email-only).
  const [centreSplit, setCentreSplit] = useState<CentreSplit>('none');
  // Field selected in either pane — drives Read ↔ form cross-highlight AND
  // The field currently armed to receive a value picked off the document.
  const [activeField, setActiveField] = useState<BillFieldKey | null>(null);
  // "Pick from PDF" (p1e): the most recent picked value addressed to a field,
  // handed to the form to apply to ``activeField``.
  const [captured, setCaptured] = useState<
    { field: BillFieldKey; value: string; nonce: number } | null
  >(null);
  // Value-first: a value grabbed off the document while NO field was armed yet.
  // The next field the operator arms/focuses receives it. This is the order
  // operators kept reaching for ("select it from the PDF and it auto fills").
  const [pendingPick, setPendingPick] = useState<{ value: string } | null>(null);
  // In-place edit on the PDF (2026-07-24 "this edit function still does not
  // work"): clicking a field's highlight box opens an editor OVER the box.
  // ``value`` null = prefill the field's current value (bare click); a string
  // = the drag-swept text (shown for confirmation instead of silently
  // replacing the field). Commit funnels into the same ``captured`` pipeline.
  const [inlineEdit, setInlineEdit] = useState<
    { field: BillFieldKey; value: string | null; nonce: number } | null
  >(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [masterNavBottom, setMasterNavBottom] = useState(
    () => readBillsLayoutPrefs().masterNavPosition === 'bottom',
  );

  useEffect(() => {
    const sync = () => {
      setMasterNavBottom(readBillsLayoutPrefs().masterNavPosition === 'bottom');
    };
    window.addEventListener('quotech:bills-layout-changed', sync);
    return () => window.removeEventListener('quotech:bills-layout-changed', sync);
  }, []);

  // A new bill invalidates any cross-highlight + capture state.
  useEffect(() => {
    setActiveField(null);
    setCaptured(null);
    setPendingPick(null);
    setInlineEdit(null);
  }, [selectedId]);

  // The picked value must be routed to whatever field is currently armed, but
  // the pick handler is created once (stable identity for the document tree).
  // Reading state through refs avoids both a stale closure AND the React
  // anti-pattern of calling a setter from inside another setter's updater
  // (which React can drop — the original "I can't get it into the description"
  // bug). activeFieldRef = the armed field; pendingPickRef = a stashed value.
  const activeFieldRef = useRef<BillFieldKey | null>(null);
  useEffect(() => {
    activeFieldRef.current = activeField;
  }, [activeField]);
  const pendingPickRef = useRef<{ value: string } | null>(null);
  useEffect(() => {
    pendingPickRef.current = pendingPick;
  }, [pendingPick]);

  // Document → form: a value picked off the PDF fills whatever field is
  // EXPLICITLY armed; with nothing armed but the click INSIDE a known field's
  // highlight box it fills THAT field directly (click a line on the PDF → the
  // matching line item updates, zero arming); otherwise it's STASHED for the
  // next field (value-first). Routing is a pure decision so it's unit-tested
  // without rendering. autoArmedFieldRef remembers a field WE selected after
  // a region pick (vs the operator arming it) so the next region hit
  // re-routes instead of overwriting the old line.
  const autoArmedFieldRef = useRef<BillFieldKey | null>(null);
  const handlePickFromDoc = useCallback(
    (text: string, regionField?: BillFieldKey | null, gesture?: 'click' | 'drag') => {
      const armed = activeFieldRef.current;
      const armedIsAuto = armed != null && armed === autoArmedFieldRef.current;
      const route = routePickedText(
        armed,
        text,
        regionField ?? null,
        armedIsAuto,
        gesture ?? 'click',
      );
      if (route.type === 'edit') {
        // Open the in-place editor over the field's box; also select the
        // field so the sidebar highlights + scrolls to the matching row.
        setInlineEdit({ field: route.field, value: route.value, nonce: Date.now() });
        setActiveField(route.field);
        autoArmedFieldRef.current = route.field;
      } else if (route.type === 'fill') {
        setCaptured({ field: route.field, value: route.value, nonce: Date.now() });
        // Region-routed pick: also select the field so the sidebar highlights
        // and scrolls to the row that just received the value.
        if (route.field !== armed) {
          setActiveField(route.field);
          autoArmedFieldRef.current = route.field;
        }
      } else {
        setPendingPick({ value: route.value });
      }
    },
    [],
  );

  // Arm a field for capture (crosshair click OR focus). If a value was already
  // grabbed off the document (value-first), drop it into this field now.
  const armField = useCallback((field: BillFieldKey | null) => {
    setActiveField(field);
    // Operator picked this field themselves → it now outranks region hits.
    autoArmedFieldRef.current = null;
    if (!field) return;
    const stash = takeStashForField(field, pendingPickRef.current?.value ?? null);
    if (stash != null) {
      setCaptured({ field, value: stash, nonce: Date.now() });
      setPendingPick(null);
    }
  }, []);

  // Clear the armed field + any stashed value (Esc / Clear on the doc banner).
  const clearCapture = useCallback(() => {
    setActiveField(null);
    setPendingPick(null);
  }, []);

  const captureLabel = useMemo(() => humanizeField(activeField), [activeField]);

  // Server-side search: the raw input value + a debounced copy that actually
  // drives the query. Debouncing (300ms) keeps a request from firing on every
  // keystroke while the operator is still typing.
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // No ``order`` param: the backend default is received order, so the
  // list is always newest-first.
  const list = useBillsList(
    { status, q: debouncedSearch, flag },
    refetchIntervalMs,
  );
  const detail = useBillDetail(selectedId);
  const actions = useBillActions();

  const setSelectedId = useCallback(
    (next: string | null) => {
      setSelectedIdRaw(next);
      onSelectionChange?.(next);
    },
    [onSelectionChange],
  );

  // Auto-open the latest bill on first load so the operator lands on real
  // content instead of the empty "Select a bill…" pane (ask 2026-06-22). The
  // list is newest-first, so the latest = bills[0]. One-shot: a deep-link or
  // any existing selection wins, and once we've opened one we never re-pick —
  // so clearing the selection later (e.g. after reclassify) is respected.
  const didAutoSelectRef = useRef(false);
  useEffect(() => {
    if (didAutoSelectRef.current) return;
    // Wait for the first list load to resolve before deciding.
    if (list.loading && !list.data) return;
    const next = pickDefaultBillId(list.data?.bills, { initialBillId, selectedId });
    if (next) {
      didAutoSelectRef.current = true;
      setSelectedId(next);
    } else if (initialBillId || selectedId) {
      // A bill is already chosen — record that we're done so a later poll
      // doesn't try to auto-pick. (An empty list with no selection leaves the
      // guard unset so a bill arriving on a later poll still auto-opens.)
      didAutoSelectRef.current = true;
    }
  }, [list.loading, list.data, initialBillId, selectedId, setSelectedId]);

  /**
   * After any successful mutation, optimistically replace the detail in
   * place AND refresh the list (status counts may have changed).
   */
  const applyMutationResult = useCallback(
    (updated: BillDetail | null) => {
      if (!updated) return;
      detail.setData(updated);
      list.refetch();
    },
    [detail, list],
  );

  const handleSave = useCallback(
    async (patch: BillEditablePatch) => {
      if (!selectedId) return;
      const result = await actions.update(selectedId, patch);
      applyMutationResult(result);
    },
    [actions, selectedId, applyMutationResult],
  );

  // Autosave sink (edit in PDF/sidebar → DB without pressing Save). Unlike
  // handleSave this deliberately does NOT replace the detail with the
  // response: the panel keeps the pre-edit bill as its undo baseline so ⌘Z
  // still reverts to the original values (and those reverts autosave too).
  // The list still refetches so row totals/vendor stay fresh.
  const handleAutoSave = useCallback(
    async (patch: BillEditablePatch) => {
      if (!selectedId) return;
      const result = await actions.update(selectedId, patch);
      if (!result) throw new Error('Autosave failed');
      list.refetch();
    },
    [actions, selectedId, list],
  );

  // Live merged (bill + unsaved draft) preview from the edit panel — feeds
  // the PDF's derived highlight regions so they track edits as you type.
  const [draftPreview, setDraftPreview] = useState<Partial<BillDetail> | null>(null);
  useEffect(() => {
    setDraftPreview(null);
  }, [selectedId]);

  // Editor prefill: drag-swept text when the pick carried one, otherwise the
  // field's CURRENT value (draft-merged, so an unsaved edit shows correctly).
  const editBase = draftPreview ?? detail.data;
  const inlineEditProp = useMemo(() => {
    if (!inlineEdit) return null;
    return {
      field: inlineEdit.field,
      value: inlineEdit.value ?? billFieldValue(editBase, inlineEdit.field),
      nonce: inlineEdit.nonce,
    };
  }, [inlineEdit, editBase]);
  const editBaseRef = useRef<Partial<BillDetail> | null>(null);
  useEffect(() => {
    editBaseRef.current = editBase;
  }, [editBase]);

  // Commit → the same captured→draft→autosave pipeline picks use, so the
  // sidebar row updates instantly and the DB follows ~1.2s later. Unchanged
  // values (e.g. an open-then-blur) commit nothing.
  const handleInlineEditCommit = useCallback((field: BillFieldKey, value: string) => {
    setInlineEdit(null);
    if (billFieldValue(editBaseRef.current, field) === value) return;
    setCaptured({ field, value, nonce: Date.now() });
  }, []);
  const handleInlineEditCancel = useCallback(() => setInlineEdit(null), []);

  const handleApprove = useCallback(async () => {
    if (!selectedId) return;
    const result = await runWithDuplicateConfirm(
      'Approve',
      (force) => actions.approve(selectedId, force ? { force: true } : undefined),
    );
    applyMutationResult(result);
  }, [actions, selectedId, applyMutationResult]);

  const handleReject = useCallback(
    async (reason: string) => {
      if (!selectedId) return;
      const result = await actions.reject(selectedId, reason);
      applyMutationResult(result);
    },
    [actions, selectedId, applyMutationResult],
  );

  const handlePushToXero = useCallback(async () => {
    if (!selectedId) return;
    const result = await runWithDuplicateConfirm(
      'Push to Xero',
      (force) => actions.pushToXero(selectedId, force ? { force: true } : undefined),
    );
    applyMutationResult(result);
  }, [actions, selectedId, applyMutationResult]);

  // Set the linked supplier's payment term, then refresh the bill — the
  // backend re-derives this bill's due_date from the new term, so the panel
  // must pick up the recomputed due_date + predicted_due_date + the
  // supplier_detail term fields.
  const handleSetSupplierTerm = useCallback(
    async (input: SetSupplierTermInput) => {
      if (!selectedId) return;
      const result = await actions.setSupplierTerm(selectedId, input);
      applyMutationResult(result);
    },
    [actions, selectedId, applyMutationResult],
  );

  // Mark the selected bill paid / unpaid. The list refresh keeps the "Paid"
  // pill on the row in sync with the detail.
  const handleMarkPaid = useCallback(
    async (input: MarkBillPaidInput) => {
      if (!selectedId) return;
      const result = await runWithDuplicateConfirm(
        'Mark as paid',
        (force) => actions.markPaid(
          selectedId, force ? { ...input, force: true } : input,
        ),
      );
      applyMutationResult(result);
    },
    [actions, selectedId, applyMutationResult],
  );

  /*
   * Re-run AI extraction on the currently-selected bill's source
   * email. Needed because the original prompt (jake, 2026-05-03 fix)
   * returned null for every dated/money field on customer statements
   * — the user has to be able to retry once the prompt is patched.
   * Uses the email_id stored on the bill detail rather than the bill
   * id because the extraction API is keyed on the source email.
   */
  const handleReExtract = useCallback(async () => {
    const emailId = detail.data?.email_id;
    if (!emailId) return;
    const result = await actions.reExtract(emailId);
    applyMutationResult(result);
  }, [actions, detail.data?.email_id, applyMutationResult]);

  /*
   * Reclassify the selected bill's email out of the AP queue. After
   * the bulk-action endpoint returns, the row will be swept by the
   * backend's orphan cleanup on the next list GET (see
   * BillListView._cleanup_orphaned_pending) — so we just refetch the
   * list and clear the local selection. We pass the emailId off the
   * list row when called via right-click (so it works even before
   * the row is selected); when called from the panel dropdown we
   * fall back to the detail's emailId.
   */
  const handleReclassify = useCallback(
    async (emailId: string, category: string) => {
      const ok = await actions.setEmailCategory(emailId, category);
      if (!ok) return;
      const wasCurrent = detail.data?.email_id === emailId;
      if (wasCurrent) {
        setSelectedId(null);
        detail.setData(null);
      }
      list.refetch();
    },
    [actions, detail, list, setSelectedId],
  );

  /*
   * Restore a cascade fail-closed bill (quarantined / awaiting_document /
   * needs_fetch / needs_review) back to the actionable queue. The backend
   * moves it to its pre_quarantine_status; we refetch so it leaves the
   * review tab and rejoins the pipeline list. If the restored bill was the
   * one open in the right pane, refresh its detail so the status pill
   * updates in place.
   */
  const handleRestore = useCallback(
    async (billId: string) => {
      const restored = await actions.restore(billId);
      if (!restored) return;
      if (detail.data?.id === billId) detail.setData(restored);
      list.refetch();
    },
    [actions, detail, list],
  );

  /*
   * Retry the machine read on ONE blocked row, straight from the list —
   * the operator shouldn't have to open a bill just to unstick it.
   */
  const handleRetryRead = useCallback(
    async (row: BillListRow) => {
      if (!row.email_id) return;
      const result = await actions.reExtract(row.email_id);
      if (result && detail.data?.id === row.id) detail.setData(result);
      list.refetch();
    },
    [actions, detail, list],
  );

  /*
   * Bulk actions (queue redesign). The panel has ALREADY split the
   * selection into eligible/skipped and shown the operator the preview —
   * this only executes on the eligible rows. Sequential on purpose: the
   * backend endpoints are per-bill, the batches are operator-sized (a
   * screenful, not thousands), and sequencing keeps failures attributable
   * row by row rather than half a parallel volley vanishing.
   */
  const handleBulkAction = useCallback(
    async (action: BulkAction, rows: BillListRow[]) => {
      setBulkBusy(true);
      try {
        for (const row of rows) {
          if (action === 'retry') {
            if (row.email_id) await actions.reExtract(row.email_id);
          } else if (action === 'restore') {
            await actions.restore(row.id);
          } else {
            await actions.reject(row.id, 'Dismissed from the queue (bulk)');
          }
        }
      } finally {
        setBulkBusy(false);
        list.refetch();
      }
    },
    [actions, list],
  );

  /*
   * Layout: explicit flex (NOT grid) with `min-h-0` on every flex
   * child — without that guard, browsers fall back to `min-height:
   * auto` which lets each pane grow to its own content height,
   * which in turn drags the whole workspace past the viewport and
   * the chrome scrolls instead of the pane (the bug jake reported
   * 2026-05-03).
   *
   * Responsive collapse rules (reworked 2026-06-19 after the operator
   * reported "where did the bills go?"): all three panes (left 288 +
   * centre 380 + right 360 = 1028) do NOT fit a ~960px content area on a
   * 1024px laptop (the icon rail eats ~64px), so exactly ONE rail must
   * yield below `xl`. An earlier pass hid the LEFT list below `xl` and
   * kept the right form — but that made the bills list (the primary
   * navigation) vanish at the most common laptop width, so the workspace
   * looked empty/broken. The list is the backbone of the workspace, so
   * the EDIT FORM is the rail that yields instead:
   *   - Centre pane is the priority — clamp it at `min-w-[380px]` so the
   *     invoice never gets crushed by the rails.
   *   - Left rail (BillsList) shows from `md` (≥768px) — it's how you find
   *     and pick a bill, so it stays visible across laptop widths. At 1024
   *     it's list(288) + centre(flex) with the form hidden = no clip.
   *   - Right rail (Bill form) shows from `lg` when the master nav is docked
   *     to the bottom (default on billing — frees ~240px). With the left
   *     sidebar still present it stays at `xl` so list + PDF + form don't clip.
   *     Below that breakpoint a floating "Bill details" drawer opens the
   *     edit form without losing the list + document split.
   *
   * Resizable rails (2026-06-18): both rails are now drag-resizable via
   * <ResizeHandle/> with persisted widths (usePersistentSize); the
   * inline `style.width` replaces the old fixed `w-*` classes. The
   * centre can also split vertically to show the source email below the
   * document (emailSplitOpen) with its own draggable height.
   */
  // Centre split toggles live in the document header (next to zoom). The
  // split below the document can show the source email OR the structured
  // read. Email is only offered when the bill actually has a source email.
  const canSplitEmail = Boolean(detail.data?.email_id);
  const setSplit = (mode: CentreSplit) =>
    setCentreSplit((cur) => (cur === mode ? 'none' : mode));
  const centreSplitToggle = (
    <>
      {canSplitEmail && (
        <SplitToggle
          active={centreSplit === 'email'}
          onClick={() => setSplit('email')}
          icon={<Rows2 className="h-3.5 w-3.5" />}
          label="Email"
          title={
            centreSplit === 'email'
              ? 'Hide source email'
              : 'Split view — show source email below'
          }
        />
      )}
      <SplitToggle
        active={centreSplit === 'read'}
        onClick={() => setSplit('read')}
        icon={<FileText className="h-3.5 w-3.5" />}
        label="Read"
        title={
          centreSplit === 'read'
            ? 'Hide structured read'
            : 'Split view — show the structured read below'
        }
      />
    </>
  );

  // When the master nav is bottom-docked we reclaim horizontal space, so the
  // right edit rail can appear one breakpoint earlier (lg vs xl).
  const rightRailInlineClass = masterNavBottom ? 'hidden lg:flex' : 'hidden xl:flex';
  const rightHandleVisibleClass = masterNavBottom ? 'hidden lg:block' : 'hidden xl:block';
  const detailsFabClass = masterNavBottom ? 'flex lg:hidden' : 'flex xl:hidden';
  const detailsDrawerShellClass = masterNavBottom ? 'lg:hidden' : 'xl:hidden';
  // Match the Tailwind gate above so we mount BillDataPanel in EXACTLY one
  // place. Rendering `rightRailBody` in both the inline rail and the drawer
  // (even when one is `display:none`) created two independent drafts: a PDF
  // pick landed in the hidden instance, then opening the drawer mounted a
  // fresh empty panel — sidebar looked unsynced and onDraftPreview(null)
  // wiped the PDF highlight patches ("bounded boxes not working").
  const rightRailMinWidth = masterNavBottom ? 1024 : 1280;
  const rightRailInline = useMinWidth(rightRailMinWidth);
  useEffect(() => {
    if (rightRailInline) setDetailsDrawerOpen(false);
  }, [rightRailInline]);

  const rightRailBody = (
    <>
      <RightTabStrip active={rightTab} onChange={setRightTab} />
      <div className="min-h-0 flex-1">
        <PaneBoundary name="details" resetKey={selectedId}>
        {rightTab === 'bill' && (
          <BillDataPanel
            bill={detail.data}
            loading={detail.loading}
            busy={actions.state.busy}
            mutationError={actions.state.error || detail.error}
            onSave={handleSave}
            onApprove={handleApprove}
            onReject={handleReject}
            onPushToXero={handlePushToXero}
            onReExtract={handleReExtract}
            onReclassify={(category) => {
              const emailId = detail.data?.email_id;
              if (!emailId) return Promise.resolve();
              return handleReclassify(emailId, category);
            }}
            onSetSupplierTerm={handleSetSupplierTerm}
            onMarkPaid={handleMarkPaid}
            activeField={activeField}
            onFieldSelect={armField}
            captured={captured}
            onAutoSave={handleAutoSave}
            onDraftPreview={setDraftPreview}
          />
        )}
        {rightTab === 'read' && (
          <StructuredBillRead
            bill={detail.data}
            loading={detail.loading}
            activeField={activeField}
            onFieldSelect={setActiveField}
          />
        )}
        {rightTab === 'email' && <BillEmailPanel bill={detail.data} />}
        {rightTab === 'po' && <BillPoPanel bill={detail.data} />}
        </PaneBoundary>
      </div>
    </>
  );

  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden">
      {leftCollapsed ? (
        // Collapsed: a thin strip with an expand button reclaims the list's
        // ~288px for the document + data panes (operator ask 2026-06-20:
        // "I should be able to collapse the left bar").
        <div className="hidden md:flex h-full w-9 shrink-0 flex-col items-center border-r border-border bg-card/60 pt-2">
          <button
            type="button"
            onClick={() => setLeftCollapsed(false)}
            title="Show bills list"
            aria-label="Show bills list"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <PanelLeftOpen className="h-4 w-4" aria-hidden />
          </button>
          <span className="mt-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground [writing-mode:vertical-rl]">
            Bills
          </span>
        </div>
      ) : (
        <>
          <div
            className="hidden md:flex h-full min-h-0 shrink-0 flex-col"
            style={{ width: leftRailW }}
          >
            <div className="flex h-9 shrink-0 items-center justify-between border-b border-border bg-card/60 px-3">
              <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                <Rows2 className="h-3.5 w-3.5" aria-hidden />
                Bills
              </span>
              <button
                type="button"
                onClick={() => setLeftCollapsed(true)}
                title="Collapse bills list"
                aria-label="Collapse bills list"
                className="flex h-6 w-6 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <PanelLeftClose className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <PaneBoundary name="bills list" resetKey={status}>
                <BillsListPanel
                  data={list.data}
                  loading={list.loading}
                  error={list.error}
                  selectedBillId={selectedId}
                  status={status}
                  onChangeStatus={setStatus}
                  flag={flag}
                  onChangeFlag={setFlag}
                  onBulkAction={handleBulkAction}
                  bulkBusy={bulkBusy}
                  onRetryRead={handleRetryRead}
                  onSelect={setSelectedId}
                  onRefresh={() => void list.refetch()}
                  onReclassify={handleReclassify}
                  onRestore={handleRestore}
                  searchValue={searchInput}
                  onSearchChange={setSearchInput}
                  onLoadMore={list.loadMore}
                  hasMore={list.hasMore}
                  loadingMore={list.loadingMore}
                />
              </PaneBoundary>
            </div>
          </div>
          <ResizeHandle
            axis="x"
            sign={1}
            value={leftRailW}
            min={LEFT_RAIL_MIN}
            max={LEFT_RAIL_MAX}
            onChange={setLeftRailW}
            resetTo={DEFAULT_LEFT_RAIL}
            ariaLabel="Resize bills list"
            className="hidden md:block"
          />
        </>
      )}
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
        <div className="min-h-0 flex-1">
          <PaneBoundary name="document" resetKey={selectedId}>
            <DocumentViewer
              bill={detail.data}
              loading={detail.loading}
              headerSlot={centreSplitToggle}
              regionValues={draftPreview}
              activeField={activeField}
              onFieldSelect={setActiveField}
              onPick={handlePickFromDoc}
              inlineEdit={inlineEditProp}
              onInlineEditCommit={handleInlineEditCommit}
              onInlineEditCancel={handleInlineEditCancel}
              captureLabel={captureLabel}
              pickedValue={pendingPick?.value ?? null}
              onCancelCapture={clearCapture}
            />
          </PaneBoundary>
        </div>
        {centreSplit !== 'none' && (centreSplit !== 'email' || canSplitEmail) && (
          <>
            <ResizeHandle
              axis="y"
              sign={-1}
              value={emailSplitH}
              min={EMAIL_SPLIT_MIN}
              max={EMAIL_SPLIT_MAX}
              onChange={setEmailSplitH}
              resetTo={DEFAULT_EMAIL_SPLIT}
              ariaLabel="Resize split panel"
            />
            <div
              className="flex min-h-0 shrink-0 flex-col border-t border-border bg-card shadow-[0_-1px_2px_0_rgb(0_0_0/0.04)]"
              style={{ height: emailSplitH }}
            >
              <div className="flex h-9 shrink-0 items-center justify-between border-b border-border px-3">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  {centreSplit === 'email' ? (
                    <Rows2 className="h-3.5 w-3.5" aria-hidden />
                  ) : (
                    <FileText className="h-3.5 w-3.5" aria-hidden />
                  )}
                  {centreSplit === 'email' ? 'Source email' : 'Structured read'}
                </span>
                <button
                  type="button"
                  onClick={() => setCentreSplit('none')}
                  title="Hide split"
                  className="flex h-6 w-6 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  aria-label="Hide split"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
              <div className="min-h-0 flex-1">
                {centreSplit === 'email' ? (
                  <BillEmailPanel bill={detail.data} />
                ) : (
                  <StructuredBillRead
                    bill={detail.data}
                    loading={detail.loading}
                    activeField={activeField}
                    onFieldSelect={setActiveField}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <ResizeHandle
        axis="x"
        sign={-1}
        value={rightRailW}
        min={RIGHT_RAIL_MIN}
        max={RIGHT_RAIL_MAX}
        onChange={setRightRailW}
        resetTo={DEFAULT_RIGHT_RAIL}
        ariaLabel="Resize bill details panel"
        className={rightHandleVisibleClass}
      />
      <div
        className={`${rightRailInlineClass} h-full min-h-0 shrink-0 flex-col`}
        style={{ width: rightRailW }}
      >
        {rightRailInline ? rightRailBody : null}
      </div>

      {/* Narrow / left-sidebar mode: floating opener + slide-over drawer */}
      <button
        type="button"
        aria-label="Open bill details"
        title="Bill details"
        onClick={() => setDetailsDrawerOpen(true)}
        className={[
          detailsFabClass,
          'fixed bottom-[calc(var(--mobile-dock-h,0px)+4.5rem)] right-4 z-40',
          'inline-flex h-10 items-center gap-2 rounded-full border border-border bg-card px-4 text-xs font-medium text-foreground shadow-md transition-colors hover:bg-accent hover:text-accent-foreground',
        ].join(' ')}
      >
        <PanelRightOpen className="h-4 w-4" aria-hidden />
        Bill details
      </button>
      {detailsDrawerOpen && !rightRailInline && (
        <div className={`${detailsDrawerShellClass} fixed inset-0 z-50`}>
          <button
            type="button"
            aria-label="Close bill details"
            className="absolute inset-0 bg-black/40"
            onClick={() => setDetailsDrawerOpen(false)}
          />
          <div
            className="absolute inset-y-0 right-0 flex w-[min(100%,22rem)] flex-col border-l border-border bg-card shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-label="Bill details"
          >
            <div className="flex h-11 shrink-0 items-center justify-between border-b border-border px-3">
              <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                <PanelRightOpen className="h-3.5 w-3.5" aria-hidden />
                Bill details
              </span>
              <button
                type="button"
                onClick={() => setDetailsDrawerOpen(false)}
                className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                aria-label="Close"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">{rightRailBody}</div>
          </div>
        </div>
      )}
    </div>
  );
}

const SCALAR_FIELD_LABELS: Record<string, string> = {
  vendor_name: 'Vendor',
  vendor_email: 'Vendor email',
  vendor_abn: 'ABN',
  invoice_number: 'Invoice #',
  reference: 'Reference',
  invoice_date: 'Invoice date',
  due_date: 'Due date',
  subtotal: 'Subtotal',
  gst: 'GST',
  total: 'Total',
  currency: 'Currency',
};
const LINE_COLUMN_LABELS: Record<string, string> = {
  description: 'Description',
  quantity: 'Qty',
  unit_amount: 'Unit price',
  line_total: 'Line total',
};

/** Subscribe to a min-width media query (matches Tailwind `lg`/`xl` gates). */
function useMinWidth(px: number): boolean {
  const query = `(min-width: ${px}px)`;
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia(query);
    const sync = () => setMatches(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [query]);
  return matches;
}

/** Field key → human banner label ("line.0.description" → "Line 1 · Description"). */
function humanizeField(key: BillFieldKey | null): string | null {
  if (!key) return null;
  const line = parseLineFieldKey(key);
  if (line) {
    return `Line ${line.idx + 1} · ${LINE_COLUMN_LABELS[line.column] ?? line.column}`;
  }
  return SCALAR_FIELD_LABELS[key] ?? key;
}

/** A compact pressed-state toggle for the document header's split buttons. */
function SplitToggle({
  active,
  onClick,
  icon,
  label,
  title,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={title}
      className={[
        'inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium transition-colors',
        active
          ? 'border-primary/30 bg-primary/10 text-primary'
          : 'border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground',
      ].join(' ')}
    >
      {icon}
      <span className="hidden lg:inline">{label}</span>
    </button>
  );
}

function RightTabStrip({
  active,
  onChange,
}: {
  active: RightTab;
  onChange: (next: RightTab) => void;
}) {
  const tabs: Array<{ id: RightTab; label: string }> = [
    { id: 'bill', label: 'Bill' },
    { id: 'read', label: 'Read' },
    { id: 'email', label: 'Email' },
    { id: 'po', label: 'PO' },
  ];
  return (
    <nav
      role="tablist"
      aria-label="Bill detail panels"
      className="flex h-11 shrink-0 items-center border-b border-l border-border bg-card px-2"
    >
      {/* Segmented control — one muted track behind the active pill reads
          as a single premium unit rather than four loose buttons. Tabs share
          the width equally so the strip fills the rail cleanly. */}
      <div className="flex w-full items-center gap-0.5 rounded-lg bg-muted/60 p-0.5">
        {tabs.map((t) => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(t.id)}
              className={[
                'inline-flex h-7 flex-1 items-center justify-center rounded-md px-3 text-xs font-medium transition-colors',
                isActive
                  ? 'bg-card text-foreground shadow-sm ring-1 ring-inset ring-border'
                  : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
